// منطقِ فهرستِ آدم‌ها: چند نفر توی گروهن، و کی «آشنایی که عضوه» حساب میشه.
//
// عمداً خالص و بدون ایمپورت، تا با `npm test` تست شه. هر دو تابعِ این‌جا یه
// بار توی محیط واقعی شکستن و تست‌هاشون همون شکست رو قفل می‌کنن.
import type { Group } from '../domain/types.ts';

/**
 * تعداد اعضای گروه.
 *
 * ═══ باگی که این تابع بستش ═══
 * قبلاً شمارش با **تطبیق** انجام می‌شد: فهرستِ دوستان و مخاطبین گرفته می‌شد و
 * هر کدوم که عضوِ گروه بود شمرده می‌شد. یعنی عدد به چیزی وابسته بود که ربطی
 * بهش نداشت — و وقتی مایگریشن ۰۰۲۱ شماره‌ی مخاطبین رو پاک کرد، اون فهرست
 * خالی شد و گروهِ سه‌نفره «۰» نشون داد.
 *
 * خودِ گروه می‌دونه چند تا عضو داره. هیچ فهرستِ بیرونی‌ای لازم نیست.
 */
export function groupMemberCount(g: Pick<Group, 'memberUserIds' | 'memberContactIds'>): number {
  return g.memberUserIds.length + g.memberContactIds.length;
}

/** یه آشنا که حساب اپ داره. */
export interface KnownPerson {
  userId: string;
  username?: string | null;
  displayName: string;
  avatarUrl?: string | null;
}

/**
 * «آشناهایی که عضو اپن ولی هنوز دوست نشدیم».
 *
 * ═══ باگی که این تابع بستش ═══
 * این فهرست فقط از مخاطبینِ **ذخیره‌شده روی سرور** ساخته می‌شد. وقتی دفترچه
 * از سرور پاک شد، فهرست خالی شد و دکمه‌ی «درخواست دوستی» هیچ‌جا دیده نمی‌شد —
 * پس کاربر نمی‌تونست درخواست بده و فکر می‌کرد داده و نرسیده.
 *
 * حالا دو منبع داره و هر کدوم که باشه کافیه:
 *   discovered  کشف‌های ثبت‌شده روی سرور (بدون هیچ داده‌ی شخصی، فقط دو شناسه)
 *   local       تطبیق‌های دفترچه‌ی روی گوشی
 *
 * هر کسی که **از قبل دوسته یا درخواستِ باز داره** بیرون می‌مونه: کارِ تازه‌ای
 * برای اون نیست.
 */
export function peopleToInvite(args: {
  discovered: KnownPerson[];
  local: KnownPerson[];
  /** شناسه‌ی کاربرانی که دوستِ تاییدشده‌ان یا درخواستِ باز دارن. */
  excludeUserIds: Iterable<string>;
  /** خودم. */
  meId?: string | null;
}): KnownPerson[] {
  const skip = new Set(args.excludeUserIds);
  if (args.meId) skip.add(args.meId);

  const out = new Map<string, KnownPerson>();
  for (const p of [...args.discovered, ...args.local]) {
    if (!p.userId || skip.has(p.userId) || out.has(p.userId)) continue;
    out.set(p.userId, p);
  }
  return [...out.values()]
    .sort((a, b) => (a.displayName || '').localeCompare(b.displayName || ''));
}

/**
 * اعضا اول، بقیه بعد — با حفظِ ترتیبِ قبلیِ هر گروه.
 *
 * چرا لازم شد: وقتی گروهی رو برای چیدن باز می‌کنی، کسایی که از قبل توشن
 * باید بالا باشن. وگرنه توی فهرستِ صدنفره باید دنبالشون بگردی تا بفهمی
 * کی هست و کی نیست.
 *
 * پایداری مهمه: دو بار صدا زدنش با همون ورودی باید همون خروجی رو بده،
 * وگرنه با هر لمس فهرست زیرِ انگشتِ کاربر می‌پره.
 */
export function membersFirst<T>(rows: T[], isIn: (row: T) => boolean): T[] {
  const yes: T[] = [];
  const no: T[] = [];
  for (const r of rows) (isIn(r) ? yes : no).push(r);
  return [...yes, ...no];
}

/* ــــ از چه راهی خبردار می‌شود ــــ */

export type Reach = 'app' | 'sms' | 'email' | 'none';

/**
 * وقتی سیگنالی بسازم، این آدم از چه راهی خبردار می‌شود.
 *
 * ترتیب اهمیت دارد و از «مطمئن‌تر» به «کم‌مطمئن‌تر» است:
 *   app   - حساب دارد، پس اعلان توی خودِ اپ می‌گیرد. رایگان و فوری.
 *   sms   - حساب ندارد ولی شماره دارد. پیامک دستِ خودِ کاربر می‌رود و
 *           هزینه‌ی اپراتور دارد.
 *   email - نه حساب نه شماره، فقط ایمیل.
 *   none  - هیچ راهی نیست؛ اسمش هست ولی خبردار نمی‌شود.
 *
 * `none` مهم‌ترین حالت است: بدون نشان دادنش، کاربر فکر می‌کند دعوت رفته
 * در حالی که هیچ‌جا نرفته.
 */
export function reachOf(p: {
  isAppUser?: boolean | null;
  phones?: readonly string[] | null;
  emails?: readonly string[] | null;
}): Reach {
  if (p.isAppUser) return 'app';
  if (p.phones && p.phones.length > 0) return 'sms';
  if (p.emails && p.emails.length > 0) return 'email';
  return 'none';
}

/**
 * چه کسانی توی حالتِ چیدنِ گروه دیده می‌شوند.
 *
 * باگی که این را لازم کرد: کلِ استخر نشان داده می‌شد — یعنی هر پونصد
 * مخاطبِ گوشی — و چون ردیف‌ها با ردیفِ دوستان یکسان بودند، کاربر فکر
 * می‌کرد همه‌ی مخاطبینش دوست شده‌اند.
 *
 * بدون جستجو: فقط اعضای فعلی و دوستان. با جستجو: هر کسی از استخر که
 * اسمش بخورد — چون افزودنِ مخاطبِ دوست‌نشده هم باید ممکن بماند.
 */
export function pickCandidates<T extends { name: string }>(args: {
  pool: readonly T[];
  friends: readonly T[];
  isMember: (row: T) => boolean;
  query?: string;
}): T[] {
  const needle = (args.query ?? '').trim().toLowerCase();
  const friendSet = new Set(args.friends);
  const base = needle
    ? args.pool.filter((r) => r.name.toLowerCase().includes(needle))
    : args.pool.filter((r) => args.isMember(r) || friendSet.has(r));
  return membersFirst(base, args.isMember);
}
